import { useState, useEffect } from "react";
import { API } from "../../api";

/**
 * Parental Controls setup modal — shown to under-18 users after the AgeGate.
 *
 * 3 main states this modal handles:
 *  1. Not set up yet → show setup form (parent email + PIN + per-feature toggle matrix)
 *  2. Set up but VPC pending (child only) → show "We've emailed your parent" waiting screen
 *  3. Already set up + confirmed → show summary + "manage" entry point
 *
 * Per founder decision Feb 2026: NO features are permanently disabled. We show smart
 * recommendations and let the parent override anything.
 */

const FEATURE_META = {
  chat:               { label: "Chat with Aura",           emoji: "💬", desc: "Text conversations (always age-appropriate)" },
  voice:              { label: "Voice calls",              emoji: "🎙️", desc: "Real-time voice chat with Aura" },
  vision:             { label: "Vision (photo react)",     emoji: "📷", desc: "Aura reacts to photos taken or shared" },
  photo_library:      { label: "Pick from photo library",  emoji: "🖼️", desc: "Choose existing photos to share" },
  social_integration: { label: "Music & social",           emoji: "🎵", desc: "Connect Spotify and other services" },
  daily_checkin:      { label: "Daily check-ins",          emoji: "☀️", desc: "Morning/evening voice or text moments" },
};

const ACCESS_LABELS = {
  unrestricted: { label: "Allowed", color: "#86efac", desc: "Child can use freely" },
  pin:          { label: "PIN required", color: "#fcd34d", desc: "Child needs your PIN" },
  blocked:      { label: "Off", color: "#fca5a5", desc: "Not available at all" },
};

export function ParentalSetupModal({ user, onClose, onComplete }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Form state
  const [parentEmail, setParentEmail] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [matrix, setMatrix] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Phase 23 — Emergency contacts. Up to 3 slots. At least 1 strongly encouraged.
  // Each: name, relationship (Parent/Guardian/Sibling/etc.), email, optional phone,
  // optional nicknames so Aura can recognize them in chat when the user mumbles.
  const [emergencyContacts, setEmergencyContacts] = useState([
    { name: "", relationship: "Parent / Guardian", email: "", phone: "", nicknames: "" },
    { name: "", relationship: "Parent / Guardian", email: "", phone: "", nicknames: "" },
  ]);
  const updateEC = (i, field, value) => {
    setEmergencyContacts((prev) => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  };
  const addECSlot = () => {
    if (emergencyContacts.length < 3) {
      setEmergencyContacts((prev) => [...prev, { name: "", relationship: "Trusted adult", email: "", phone: "", nicknames: "" }]);
    }
  };

  useEffect(() => {
    fetch(`${API}/parental/status`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        setStatus(d);
        // pre-fill matrix with recommended defaults
        setMatrix(d.access_matrix || d.recommended_access_matrix || {});
        setLoading(false);
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load");
        setLoading(false);
      });
  }, []);

  const ageBand = status?.age_band;
  const setupDone = status?.setup_completed;
  const vpcPending = status?.vpc_pending;

  const handleSubmit = async () => {
    setError("");
    if (!parentEmail.trim() || !parentEmail.includes("@")) {
      setError("Please enter your parent's email address");
      return;
    }
    if (parentEmail.trim().toLowerCase() === (user?.email || "").trim().toLowerCase()) {
      setError("Parent email must be different from your account email");
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setError("PIN must be exactly 4 digits");
      return;
    }
    if (pin !== pinConfirm) {
      setError("PINs don't match");
      return;
    }
    setSubmitting(true);
    try {
      // Phase 23 — package the emergency contacts the parent filled in. Skip blank slots.
      const ec_payload = emergencyContacts
        .map((c) => ({
          name: (c.name || "").trim(),
          relationship: (c.relationship || "").trim() || "Trusted adult",
          email: (c.email || "").trim().toLowerCase(),
          phone: (c.phone || "").trim(),
          nicknames: (c.nicknames || "")
            .split(",")
            .map((n) => n.trim())
            .filter(Boolean),
        }))
        .filter((c) => c.name && c.email && c.email.includes("@"));
      const res = await fetch(`${API}/parental/setup`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          parent_email: parentEmail.trim().toLowerCase(),
          parent_pin: pin,
          access_matrix: matrix,
          emergency_contacts: ec_payload,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || "Couldn't save");
        setSubmitting(false);
        return;
      }
      onComplete && onComplete(data);
    } catch (err) {
      setError(err?.message || "Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay parental-overlay" data-testid="parental-setup-modal">
        <div className="payment-modal parental-modal">
          <div className="modal-icon">🛡️</div>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  // VPC pending — waiting for parent to click email link
  if (setupDone && vpcPending) {
    return (
      <div className="modal-overlay parental-overlay" data-testid="parental-vpc-pending">
        <div className="payment-modal parental-modal">
          <div className="modal-icon">📧</div>
          <h2>Waiting for a grown-up</h2>
          <p className="modal-subtitle">
            We sent <strong>{status.parent_email}</strong> an email with a confirmation link.
          </p>
          <p className="parental-explainer">
            Once they click it, your Aura will be all set up with the safety settings they choose.
            Until then, you can chat with Aura — other features will unlock after they confirm.
          </p>
          <button className="ghost-btn small" onClick={onClose} data-testid="parental-vpc-pending-close">
            Got it
          </button>
          <p className="parental-fineprint">
            The email might take a minute to arrive — also check the spam folder. 💜
          </p>
        </div>
      </div>
    );
  }

  // Already set up + confirmed — show summary
  if (setupDone && !vpcPending) {
    return (
      <div className="modal-overlay parental-overlay" onClick={onClose} data-testid="parental-summary">
        <div className="payment-modal parental-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-icon">🛡️</div>
          <h2>Parental controls</h2>
          <p className="modal-subtitle">
            Set up by <strong>{status.parent_email}</strong>
          </p>
          <div className="parental-matrix-summary">
            {(status.features || Object.keys(FEATURE_META)).map((f) => {
              const access = status.access_matrix?.[f] || "unrestricted";
              const meta = FEATURE_META[f] || { label: f, emoji: "•" };
              const a = ACCESS_LABELS[access] || {};
              return (
                <div className="parental-matrix-row" key={f} data-testid={`parental-row-${f}`}>
                  <span className="parental-row-label">{meta.emoji} {meta.label}</span>
                  <span className="parental-row-access" style={{ color: a.color }}>{a.label}</span>
                </div>
              );
            })}
          </div>
          <p className="parental-fineprint">
            Only a grown-up with the PIN can change these. Ask them if anything needs to be adjusted.
          </p>
          <button className="ghost-btn small" onClick={onClose} data-testid="parental-summary-close">
            Close
          </button>
        </div>
      </div>
    );
  }

  // Setup flow
  return (
    <div className="modal-overlay parental-overlay" data-testid="parental-setup-modal">
      <div className="payment-modal parental-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-icon">🛡️</div>
        <h2>{ageBand === "child" ? "Hi, grown-up!" : "Set up parental controls"}</h2>
        <p className="modal-subtitle">
          {ageBand === "child"
            ? `${user?.name || "Your child"} signed up for Aura. Let's set this up together so they're safe.`
            : "Optional — turn this on if a parent wants visibility and control."}
        </p>

        <div className="parental-section">
          <label className="parental-label">Your email (the parent's)</label>
          <input
            type="email"
            className="init-input"
            placeholder="parent@example.com"
            value={parentEmail}
            onChange={(e) => setParentEmail(e.target.value)}
            data-testid="parental-parent-email"
            autoComplete="email"
          />
          <p className="parental-hint">Must be different from {user?.name || "your child"}&apos;s email.</p>
        </div>

        <div className="parental-section">
          <label className="parental-label">Set a 4-digit PIN</label>
          <div className="parental-pin-row">
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="4"
              className="init-input parental-pin-input"
              placeholder="••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              data-testid="parental-pin-input"
            />
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength="4"
              className="init-input parental-pin-input"
              placeholder="Confirm"
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
              data-testid="parental-pin-confirm"
            />
          </div>
          <p className="parental-hint">You&apos;ll use this PIN whenever {user?.name || "your child"} wants to unlock a restricted feature.</p>
        </div>

        <div className="parental-section parental-emergency-section" data-testid="parental-emergency-section">
          <label className="parental-label">Emergency contacts (1–3 people)</label>
          <p className="parental-hint" style={{ marginBottom: "0.6rem" }}>
            People Aura will quietly email if she notices a serious safety signal in {user?.name || "your child"}'s conversations.
            Aura never shares what was said — just a "they might want a check-in tonight" heads-up.
            Add nicknames so Aura recognizes them in chat (helps when people mumble or use casual names — no judgment, we all do it).
          </p>
          {emergencyContacts.map((c, i) => (
            <div key={i} className="emergency-slot" data-testid={`emergency-slot-${i}`}>
              <div className="emergency-slot-header">
                <span className="emergency-slot-num">#{i + 1}</span>
                <input
                  type="text"
                  className="init-input emergency-relationship"
                  placeholder="Parent / Guardian / Sibling"
                  value={c.relationship}
                  onChange={(e) => updateEC(i, "relationship", e.target.value)}
                  data-testid={`emergency-relationship-${i}`}
                />
              </div>
              <input
                type="text"
                className="init-input"
                placeholder="Full name *"
                value={c.name}
                onChange={(e) => updateEC(i, "name", e.target.value)}
                data-testid={`emergency-name-${i}`}
                autoComplete="off"
              />
              <input
                type="email"
                className="init-input"
                placeholder="Their email *"
                value={c.email}
                onChange={(e) => updateEC(i, "email", e.target.value)}
                data-testid={`emergency-email-${i}`}
                autoComplete="off"
              />
              <input
                type="tel"
                className="init-input"
                placeholder="Phone (optional, for future call features)"
                value={c.phone}
                onChange={(e) => updateEC(i, "phone", e.target.value)}
                data-testid={`emergency-phone-${i}`}
                autoComplete="off"
              />
              <input
                type="text"
                className="init-input"
                placeholder="Nicknames Aura might hear (Mama, Pops, M, comma-separated)"
                value={c.nicknames}
                onChange={(e) => updateEC(i, "nicknames", e.target.value)}
                data-testid={`emergency-nicknames-${i}`}
                autoComplete="off"
              />
            </div>
          ))}
          {emergencyContacts.length < 3 && (
            <button
              type="button"
              className="ghost-btn small"
              onClick={addECSlot}
              data-testid="emergency-add-slot"
            >
              + Add another contact
            </button>
          )}
        </div>

        <div className="parental-section">
          <label className="parental-label">What can {user?.name || "they"} access?</label>
          <p className="parental-hint" style={{ marginBottom: "0.6rem" }}>
            Our recommendations are pre-selected. You can change any of them — nothing is locked.
          </p>
          <div className="parental-matrix">
            {Object.entries(FEATURE_META).map(([feature, meta]) => (
              <div className="parental-matrix-edit-row" key={feature} data-testid={`parental-edit-row-${feature}`}>
                <div className="parental-feature-info">
                  <span className="parental-feature-title">{meta.emoji} {meta.label}</span>
                  <span className="parental-feature-desc">{meta.desc}</span>
                </div>
                <div className="parental-feature-radios">
                  {Object.entries(ACCESS_LABELS).map(([level, l]) => (
                    <button
                      key={level}
                      type="button"
                      className={`parental-radio ${matrix[feature] === level ? "selected" : ""}`}
                      onClick={() => setMatrix({ ...matrix, [feature]: level })}
                      style={matrix[feature] === level ? { borderColor: l.color, color: l.color } : {}}
                      data-testid={`parental-radio-${feature}-${level}`}
                      title={l.desc}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="error-message" data-testid="parental-setup-error">{error}</div>}

        <button
          className="init-btn"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="parental-setup-submit"
        >
          {submitting
            ? "Saving…"
            : ageBand === "child"
              ? "Send myself confirmation email"
              : "Turn on parental controls"}
        </button>

        {ageBand === "teen" && (
          <button
            type="button"
            className="ghost-btn small"
            onClick={onClose}
            data-testid="parental-setup-skip"
          >
            Maybe later
          </button>
        )}

        <p className="parental-fineprint">
          {ageBand === "child"
            ? "We'll send a one-time confirmation link to your email. You only need to click it once."
            : "You can turn this off anytime with your PIN."}
        </p>
      </div>
    </div>
  );
}


/**
 * PIN Unlock modal — shown when a child taps a PIN-restricted feature.
 * Asks the parent for the 4-digit PIN; on success, the feature works for 30 min.
 */
export function ParentalUnlockModal({ feature, onUnlocked, onCancel }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleUnlock = async () => {
    setError("");
    if (!/^\d{4}$/.test(pin)) {
      setError("PIN must be 4 digits");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/parental/unlock`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail || "Couldn't unlock");
        setSubmitting(false);
        return;
      }
      onUnlocked && onUnlocked(data);
    } catch (err) {
      setError(err?.message || "Couldn't unlock");
    } finally {
      setSubmitting(false);
    }
  };

  const featureLabel = FEATURE_META[feature]?.label || feature;

  return (
    <div className="modal-overlay parental-overlay" data-testid="parental-unlock-modal">
      <div className="payment-modal parental-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-icon">🔒</div>
        <h2>Grown-up needed</h2>
        <p className="modal-subtitle">
          To use <strong>{featureLabel}</strong>, please ask a grown-up to enter the PIN.
        </p>
        <input
          type="password"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength="4"
          className="init-input parental-pin-input"
          placeholder="••••"
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          autoFocus
          data-testid="parental-unlock-pin"
          onKeyDown={(e) => e.key === "Enter" && handleUnlock()}
        />
        {error && <div className="error-message" data-testid="parental-unlock-error">{error}</div>}
        <button
          className="init-btn"
          onClick={handleUnlock}
          disabled={submitting || pin.length !== 4}
          data-testid="parental-unlock-submit"
        >
          {submitting ? "Unlocking…" : "Unlock for 30 min"}
        </button>
        <button type="button" className="ghost-btn small" onClick={onCancel} data-testid="parental-unlock-cancel">
          Never mind
        </button>
        <p className="parental-fineprint">
          5 wrong tries = locked for 15 minutes.
        </p>
      </div>
    </div>
  );
}
